<template>
    <div>
        <DiscussionTarget />
        <b-card class="mb-3 request-glass-card">
            <b-card-header>
                <div class="d-flex justify-content-between align-items-center">
                    <h6 class="mb-0">
                        <i class="fas fa-comments"></i> Комментарии и история
                    </h6>
                    <b-button v-if="canAdd" variant="outline-primary" size="sm" @click="showAddComment = !showAddComment">
                        <i class="fas fa-plus"></i> Добавить комментарий
                    </b-button>
                </div>
            </b-card-header>
            <b-card-body>
                <!-- Форма добавления комментария -->
                <div
                    v-if="canAdd && showAddComment"
                    class="mb-3 border rounded p-3 bg-light comment-compose-dropzone"
                    :class="{ 'comment-compose-dropzone--active': isCommentDropActive }"
                    @dragenter.prevent="onCommentDragEnter"
                    @dragover.prevent="onCommentDragOver"
                    @dragleave="onCommentDragLeave"
                    @drop.prevent="onCommentDrop"
                >
                    <div v-if="isCommentDropActive" class="comment-compose-dropzone__overlay">
                        <i class="fas fa-paperclip"></i>
                        Отпустите файлы, чтобы прикрепить к комментарию
                    </div>
                    <b-form @submit.prevent="addComment">
                        <DiscussionEditor primary defer-clear ref="composer" :disabled="commentBusy"
                            v-model="newComment"
                            placeholder="Введите комментарий... (Ctrl+V для вставки файлов из буфера)"
                            required
                            @paste="onPasteToComment"
                        />
                        
                        <!-- Файлы к комментарию -->
                        <div class="mt-2">
                            <MechanicAssetFileUploadPanel
                                v-model="newCommentFiles"
                                accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar,.7z,.txt"
                            />
                            <small class="text-muted d-block mt-1">
                                Можно выбрать файлы, вставить из буфера или сделать несколько фото подряд.
                            </small>
                        </div>

                        <div class="mt-2">
                            <b-button type="submit" variant="primary" size="sm" :disabled="commentBusy">
                                <i class="fas fa-paper-plane"></i> Отправить
                            </b-button>
                            <b-button variant="outline-secondary" size="sm" @click="cancelAddComment" class="ml-2">
                                Отмена
                            </b-button>
                        </div>
                    </b-form>
                </div>

                <!-- Объединенный список комментариев и логов -->
                <div v-if="combinedItems.length">
                    <CommentItem
                        v-for="item in combinedItems"
                        :key="item._key"
                        ref="commentItems"
                        :item="item"
                        :can-edit="canEdit"
                        :request-id="requestId"
                        @editComment="editComment"
                        @removeComment="removeComment"
                        @removeFile="removeFile"
                    />
                </div>
                <div v-else class="text-muted text-center py-3">
                    <i class="fas fa-info-circle"></i>
                    Комментариев и событий пока нет
                </div>
            </b-card-body>
        </b-card>
    </div>
</template>

<script>
import {discussionFeedMixin} from "@/utils/discussions";
import CommentItem from './CommentItem.vue';
import clipboardFileMixin from '@/mixins/clipboardFileMixin.js';
import MechanicAssetFileUploadPanel from '@/views/MechanicAssets/MechanicAssetFileUploadPanel.vue';

export default {
    name: "CommentsBlock",
    mixins: [discussionFeedMixin, clipboardFileMixin],
    components: {
        CommentItem,
        MechanicAssetFileUploadPanel
    },
    props: {
        comments: {
            type: Array,
            default: () => []
        },
        history: {
            type: Array,
            default: () => []
        },
        canAdd: {type: Boolean, default: true},
        canEdit: {
            type: Boolean,
            default: false
        },
        requestId: {
            type: [Number, String],
            required: false
        }
    },
    data() {
        return {
            showAddComment: false,
            newComment: '',
            commentBusy: false,
            submission: {},
            newCommentFiles: [],
            isCommentDropActive: false,
            commentDragDepth: 0
        }
    },
    computed: {
        combinedItems() {
            // Объединяем комментарии и историю в один массив
            const comments = (this.comments || []).map(c => ({
                ...c,
                _type: 'comment',
                _key: 'comment_' + c.id,
                _timestamp: new Date(c.created_at).getTime()
            }));

            const logs = (this.history || []).map(h => ({
                ...h,
                _type: 'log',
                _key: 'log_' + h.id,
                _timestamp: new Date(h.created_at).getTime()
            }));

            // Объединяем и сортируем по дате (сначала новые)
            return [...comments, ...logs].sort((a, b) => b._timestamp - a._timestamp);
        }
    },
    methods: {
        addComment() {
            if (this.commentBusy) return;
            if (!this.newComment.trim()) {
                this.$bvToast.toast('Комментарий не может быть пустым', {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
                return;
            }

            this.commentBusy = true;
            this.$emit('addComment', {
                text: this.newComment,
                files: this.newCommentFiles,
                draft: this.submission,
                done: error => {
                    this.commentBusy = false;
                    if (error) { this.$refs.composer.error = error.response && error.response.data && (error.response.data.message || error.response.data.msg) || 'Не удалось сохранить комментарий и вложения. Повторите попытку.'; return; }
                    this.$refs.composer.clear(); this.cancelAddComment();
                }
            });
        },
        cancelAddComment() {
            this.submission = {};
            this.newComment = '';
            this.newCommentFiles = [];
            this.showAddComment = false;
        },
        editComment(data) {
            this.$emit('editComment', data);
        },
        removeComment(commentId) {
            this.$emit('removeComment', commentId);
        },
        removeFile(data) {
            this.$emit('removeFile', data);
        },
        hasDirtyState() {
            const hasNewCommentDraft = Boolean(
                (this.newComment && this.newComment.trim()) ||
                (this.newCommentFiles && this.newCommentFiles.length)
            );
            if (hasNewCommentDraft) {
                return true;
            }

            const commentItems = Array.isArray(this.$refs.commentItems)
                ? this.$refs.commentItems
                : (this.$refs.commentItems ? [this.$refs.commentItems] : []);

            return commentItems.some(item => item && typeof item.hasDirtyState === 'function' && item.hasDirtyState());
        },
        // Обработчик paste для textarea комментария
        onPasteToComment(event) {
            this.handlePaste(event, (files) => {
                this.addFilesFromClipboard(files);
            });
        },
        // Добавление файлов из буфера обмена
        addFilesFromClipboard(files) {
            this.addFilesToComment(files);
        },
        addFilesToComment(files) {
            const incoming = Array.isArray(files)
                ? files.filter(Boolean)
                : (files && typeof files.length === 'number' ? Array.from(files).filter(Boolean) : files ? [files] : []);
            if (!incoming.length) {
                return;
            }
            const byKey = f => `${f.name}|${f.size}|${f.lastModified}`;
            const map = new Map(this.newCommentFiles.map(f => [byKey(f), f]));
            incoming.forEach(f => {
                const key = byKey(f);
                if (!map.has(key)) map.set(key, f);
            });
            this.newCommentFiles = Array.from(map.values());
        },
        onCommentDragEnter(event) {
            if (!this.hasDraggedFiles(event)) {
                return;
            }
            this.commentDragDepth += 1;
            this.isCommentDropActive = true;
        },
        onCommentDragOver(event) {
            if (!this.hasDraggedFiles(event)) {
                return;
            }
            event.dataTransfer.dropEffect = 'copy';
            this.isCommentDropActive = true;
        },
        onCommentDragLeave(event) {
            if (!this.hasDraggedFiles(event)) {
                return;
            }
            this.commentDragDepth = Math.max(this.commentDragDepth - 1, 0);
            if (this.commentDragDepth === 0) {
                this.isCommentDropActive = false;
            }
        },
        onCommentDrop(event) {
            this.commentDragDepth = 0;
            this.isCommentDropActive = false;
            const files = event.dataTransfer && event.dataTransfer.files
                ? Array.from(event.dataTransfer.files)
                : [];
            this.addFilesToComment(files);
        },
        hasDraggedFiles(event) {
            const types = event && event.dataTransfer && event.dataTransfer.types
                ? Array.from(event.dataTransfer.types)
                : [];
            return types.indexOf('Files') !== -1 || types.indexOf('files') !== -1;
        }
    }
}
</script>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}

.comment-text {
    white-space: pre-wrap;
}

.gap-2 {
    gap: 0.5rem;
}

.comment-compose-dropzone {
    position: relative;
    transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
}

.comment-compose-dropzone--active {
    border-color: #007bff !important;
    background-color: #eef6ff !important;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.15);
}

.comment-compose-dropzone__overlay {
    position: absolute;
    z-index: 5;
    left: 0.75rem;
    right: 0.75rem;
    top: 0.75rem;
    bottom: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    border: 2px dashed #007bff;
    border-radius: 0.25rem;
    background: rgba(238, 246, 255, 0.94);
    color: #0056b3;
    font-weight: 600;
    text-align: center;
    pointer-events: none;
}
</style>

